import React from "react";
import {
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Space,
  InputNumber,
  Select,
  Row,
  Col,
  Typography,
  Upload,
  Divider,
  Popconfirm,
  Tooltip,
  Card,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile } from "antd/es/upload";
import type { FormInstance } from "antd/es/form";
import { useTranslation } from "react-i18next";
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import type { TransactionLine, FinanceTreeItem } from "../types/financeTransaction.types";

const { TextArea } = Input;
const { Text } = Typography;

interface FinanceTransactionFormProps {
  form: FormInstance;
  transactionLines: TransactionLine[];
  setTransactionLines: React.Dispatch<React.SetStateAction<TransactionLine[]>>;
  financeTreeOptions: { label: string; value: number; level: number }[];
  financeTreeLoading: boolean;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  handleCustomUpload: (options: any) => Promise<void>;
  handleRemove: (file: any) => Promise<void>;
  uploading: boolean;
  isViewMode?: boolean;
  isEditMode?: boolean;
  onSubmit: (values: any) => void;
  onCancel: () => void;
  saving?: boolean;
  messageApi?: any;
}

export const FinanceTransactionForm: React.FC<FinanceTransactionFormProps> = ({
  form,
  transactionLines,
  setTransactionLines,
  financeTreeOptions,
  financeTreeLoading,
  fileList,
  setFileList,
  handleCustomUpload,
  handleRemove,
  uploading,
  isViewMode = false,
  isEditMode = false,
  onSubmit,
  onCancel,
  saving = false,
  messageApi,
}) => {
  const { t } = useTranslation();

  // Add new transaction line
  const handleAddLine = () => {
    // Generate a unique key using timestamp and random number to avoid conflicts
    const newKey = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setTransactionLines([...transactionLines, { key: newKey }]);
  };

  // Remove transaction line
  const handleRemoveLine = (key: string) => {
    setTransactionLines(transactionLines.filter((line) => line.key !== key));
  };

  // Update transaction line
  const handleLineChange = (key: string, field: keyof TransactionLine, value: any) => {
    setTransactionLines(
      transactionLines.map((line) =>
        line.key === key ? { ...line, [field]: value } : line
      )
    );
  };

  // Calculate totals
  const totalDebit = transactionLines.reduce((sum, line) => sum + (line.debit || 0), 0);
  const totalCredit = transactionLines.reduce((sum, line) => sum + (line.credit || 0), 0);

  // Table columns
  const columns: ColumnsType<TransactionLine> = [
    {
      title: <Text strong>{t("label.finance_transaction.finance_item")}</Text>,
      dataIndex: "financeItemId",
      key: "financeItemId",
      width: "35%",
      render: (_, record) =>
        isViewMode ? (
          <Text>
            {financeTreeOptions.find((opt) => opt.value === record.financeItemId)?.label || "-"}
          </Text>
        ) : (
          <Select
            showSearch
            placeholder={t("placeholder.finance_transaction.select_finance_item")}
            style={{ width: "100%" }}
            loading={financeTreeLoading}
            optionFilterProp="label"
            value={record.financeItemId}
            onChange={(value) => handleLineChange(record.key, "financeItemId", value)}
            options={financeTreeOptions}
          />
        ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.line_description")}</Text>,
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (_, record) =>
        isViewMode ? (
          <Text>{record.description || "-"}</Text>
        ) : (
          <Input
            placeholder={t("placeholder.finance_transaction.line_description")}
            value={record.description}
            onChange={(e) => handleLineChange(record.key, "description", e.target.value)}
          />
        ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.debit")}</Text>,
      dataIndex: "debit",
      key: "debit",
      width: "15%",
      render: (_, record) =>
        isViewMode ? (
          <Text strong style={{ color: "#cf1322" }}>
            {record.debit ? record.debit.toFixed(2) : "-"}
          </Text>
        ) : (
          <InputNumber
            placeholder="0.00"
            style={{ width: "100%" }}
            min={0}
            precision={2}
            value={record.debit}
            onChange={(value) => handleLineChange(record.key, "debit", value || undefined)}
            disabled={!!record.credit}
          />
        ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.credit")}</Text>,
      dataIndex: "credit",
      key: "credit",
      width: "15%",
      render: (_, record) =>
        isViewMode ? (
          <Text strong style={{ color: "#3f8600" }}>
            {record.credit ? record.credit.toFixed(2) : "-"}
          </Text>
        ) : (
          <InputNumber
            placeholder="0.00"
            style={{ width: "100%" }}
            min={0}
            precision={2}
            value={record.credit}
            onChange={(value) => handleLineChange(record.key, "credit", value || undefined)}
            disabled={!!record.debit}
          />
        ),
    },
    ...(!isViewMode
      ? [
          {
            title: <Text strong>{t("label.actions")}</Text>,
            key: "action",
            width: "5%",
            align: "center" as const,
            render: (_: any, record: TransactionLine) => (
              <Tooltip title={t("button.delete")}>
                <Popconfirm
                  title={t("message.confirm_delete")}
                  onConfirm={() => handleRemoveLine(record.key)}
                  okText={t("button.confirm")}
                  cancelText={t("button.cancel")}
                  disabled={transactionLines.length <= 1}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={transactionLines.length <= 1}
                  />
                </Popconfirm>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <Row gutter={24}>
        {/* Date Field */}
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            label={<Text strong>{t("label.finance_transaction.date")}</Text>}
            name="date"
            rules={[
              {
                required: true,
                message: t("message.error.field_required"),
              },
            ]}
          >
            <DatePicker
              style={{
                width: "100%",
                borderRadius: 8,
                height: 44,
              }}
              format="DD/MM/YYYY"
              placeholder={t("placeholder.finance_transaction.date")}
              disabled={isViewMode}
            />
          </Form.Item>
        </Col>

        {/* Description Field */}
        <Col xs={24} sm={12} md={16}>
          <Form.Item
            label={<Text strong>{t("label.finance_transaction.description")}</Text>}
            name="description"
            rules={[
              {
                required: true,
                message: t("message.error.field_required"),
              },
            ]}
          >
            <TextArea
              rows={2}
              placeholder={t("placeholder.finance_transaction.description")}
              maxLength={500}
              showCount
              disabled={isViewMode}
              style={{
                borderRadius: 8,
                fontSize: 14,
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Attachments */}
      {!isViewMode && (
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              label={
                <Space>
                  <Text strong>{t("label.finance_transaction.attachments")}</Text>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                    ({t("label.finance_transaction.upload_hint")})
                  </Text>
                </Space>
              }
            >
              <Upload
                listType="picture-card"
                fileList={fileList}
                onChange={({ fileList: newFileList }) => {
                  if (newFileList[newFileList.length - 1]?.status === "done" && messageApi) {
                    messageApi.success(`${newFileList[newFileList.length - 1].name} file uploaded successfully`);
                  } else if (newFileList[newFileList.length - 1]?.status === "error" && messageApi) {
                    messageApi.error(`${newFileList[newFileList.length - 1].name} file upload failed.`);
                  }
                  setFileList(newFileList);
                }}
                onRemove={handleRemove}
                customRequest={handleCustomUpload}
                maxCount={5}
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              >
                {fileList.length >= 5 ? null : (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>
                      {t("label.finance_transaction.upload_text")}
                    </div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      )}

      <Divider
        orientation="left"
        style={{
          marginTop: 32,
          marginBottom: 24,
          borderColor: "#d9d9d9",
        }}
      >
        <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
          {t("label.finance_transaction.transaction_lines")} ({transactionLines.length})
        </Text>
      </Divider>

      {/* Transaction Lines Table */}
      <Table
        columns={columns}
        dataSource={transactionLines}
        pagination={false}
        bordered
        size="middle"
        footer={() => (
          <Row gutter={16} justify="space-between" align="middle" style={{ padding: "8px 0" }}>
            <Col>
              {!isViewMode && (
                <Button
                  type="dashed"
                  onClick={handleAddLine}
                  icon={<PlusOutlined />}
                  size="large"
                  style={{
                    borderRadius: 8,
                    height: 44,
                    fontWeight: 500,
                  }}
                >
                  {t("button.add_line")}
                </Button>
              )}
            </Col>
            <Col>
              <Space size="large">
                <Card
                  size="small"
                  style={{
                    borderRadius: 8,
                    border: "1px solid #ffccc7",
                    backgroundColor: "#fff1f0",
                    minWidth: 140,
                  }}
                >
                  <Space direction="vertical" size={0} style={{ width: "100%" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("label.finance_transaction.total_debit")}
                    </Text>
                    <Text strong style={{ fontSize: 18, color: "#cf1322" }}>
                      {totalDebit.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Space>
                </Card>
                <Card
                  size="small"
                  style={{
                    borderRadius: 8,
                    border: "1px solid #b7eb8f",
                    backgroundColor: "#f6ffed",
                    minWidth: 140,
                  }}
                >
                  <Space direction="vertical" size={0} style={{ width: "100%" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("label.finance_transaction.total_credit")}
                    </Text>
                    <Text strong style={{ fontSize: 18, color: "#3f8600" }}>
                      {totalCredit.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Space>
                </Card>
                <Card
                  size="small"
                  style={{
                    borderRadius: 8,
                    border: "1px solid #d9d9d9",
                    backgroundColor: "#fafafa",
                    minWidth: 140,
                  }}
                >
                  <Space direction="vertical" size={0} style={{ width: "100%" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("label.finance_transaction.difference")}
                    </Text>
                    <Text
                      strong
                      style={{
                        fontSize: 18,
                        color: "#262626",
                      }}
                    >
                      {(totalDebit - totalCredit).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Space>
                </Card>
              </Space>
            </Col>
          </Row>
        )}
      />

      <Divider />

      {/* Submit Button */}
      {!isViewMode && (
        <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
          <Row justify="end" align="middle">
            <Col>
              <Space size="middle">
                <Button
                  onClick={onCancel}
                  size="large"
                  style={{
                    borderRadius: 8,
                    height: 44,
                    minWidth: 100,
                  }}
                >
                  {t("button.cancel")}
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving || uploading}
                  disabled={transactionLines.length === 0}
                  size="large"
                  style={{
                    borderRadius: 8,
                    height: 44,
                    minWidth: 140,
                    fontWeight: 500,
                  }}
                >
                  {isEditMode ? t("button.save") : t("button.save_transaction")}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form.Item>
      )}
    </Form>
  );
};

